import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { Users, ChevronDown, ChevronUp, Filter } from 'lucide-react';
import apiService from '../utils/apiService';

const SubAffiliateBanners = () => {
  const { isDarkMode } = useTheme();
  useTranslation();
  
  // Campaign dropdown state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const campaignsApiCalled = useRef(false);
  
  // Category dropdown state
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const categoriesApiCalled = useRef(false);

  const [banners] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState(new Set());
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  // Filter banners based on selected filters
  const filteredBanners = banners.filter(banner => {
    if (selectedSize && banner.size !== selectedSize) return false;
    // Add more filter logic here for campaign and category when data is available
    return true;
  });

  // Group filtered banners by size
  const bannersBySize = filteredBanners.reduce((acc, banner) => {
    if (!acc[banner.size]) {
      acc[banner.size] = [];
    }
    acc[banner.size].push(banner);
    return acc;
  }, {});

  const sizes = ['All Sizes', ...Object.keys(bannersBySize)];

  // Fetch campaigns for dropdown
  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const response = await apiService.getSubCampaigns();
      if (response.success && response.data) {
        setCampaigns(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Fetch categories for dropdown
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await apiService.getBannerCategories();
      if (response.success && response.data) {
        setCategories(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    setIsLoaded(true);
    
    if (!campaignsApiCalled.current) {
      campaignsApiCalled.current = true;
      fetchCampaigns();
    }
    
    if (!categoriesApiCalled.current) {
      categoriesApiCalled.current = true;
      fetchCategories();
    }
  }, []);

  const togglePanel = (size) => {
    setExpandedPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(size)) {
        newSet.delete(size);
      } else {
        newSet.add(size);
      }
      return newSet;
    });
  };

  const getBannerHtmlCode = (banner) => {
    const fullUrl = window.location.origin + banner.imageUrl;
    const imageLink = window.location.origin; // You can customize this link
    return `<a href="${imageLink}"><img src="${fullUrl}" alt="Banner ${banner.size}" width="${banner.width}" height="${banner.height}" /></a>`;
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
      <div className={`p-3 sm:p-4 lg:p-6 ${isDarkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'}`}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-800 to-red-900 shadow-lg">
                <Users className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className={`text-lg sm:text-xl lg:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Sub Partner Banners
                </h2>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Manage sub partner marketing banners
                </p>
              </div>
            </div>
          </div>

          {/* Filter Dropdowns */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '150ms' }}>
            <div className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border-gray-800' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'} rounded-xl p-4 border shadow-lg mb-6`}>
              <div className="flex items-center space-x-2 mb-4">
                <Filter className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-800'}`} />
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filters</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Campaign Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Select Campaign
                  </label>
                  <select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    disabled={campaignsLoading}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } ${campaignsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">
                      {campaignsLoading ? 'Loading campaigns...' : 'All Campaigns'}
                    </option>
                    {campaigns.map((campaign) => (
                      <option key={campaign.campaignID} value={campaign.campaignID}>
                        {campaign.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Category Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    disabled={categoriesLoading}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    } ${categoriesLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">
                      {categoriesLoading ? 'Loading categories...' : 'All Categories'}
                    </option>
                    {categories.map((category) => (
                      <option key={category.categoryID} value={category.categoryID}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Size Filter */}
                <div>
                  <label className={`block text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Size
                  </label>
                  <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200 ${
                      isDarkMode 
                        ? 'bg-black border-gray-800 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {sizes.map((size) => (
                      <option key={size} value={size === 'All Sizes' ? '' : size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Banners by Size Panels */}
          <div className={`transition-all duration-700 ease-out ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
            {Object.keys(bannersBySize).length === 0 ? (
              <div className={`rounded-xl border p-8 text-center ${isDarkMode ? 'bg-black border-gray-800 text-gray-400' : 'bg-white border-gray-200 text-gray-500'}`}>
                No banners available
              </div>
            ) : (
            <div className="space-y-6">
              {Object.entries(bannersBySize).map(([size, sizeBanners], panelIndex) => {
                const isExpanded = expandedPanels.has(size);
                return (
                  <div
                    key={size}
                    className={`${isDarkMode ? 'bg-gradient-to-br from-black to-black border-gray-800' : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'} rounded-xl border shadow-lg`}
                    style={{ transitionDelay: `${(panelIndex + 1) * 100}ms` }}
                  >
                    {/* Panel Header */}
                    <div 
                      className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} cursor-pointer hover:${isDarkMode ? 'bg-black' : 'bg-gray-50'} transition-colors`}
                      onClick={() => togglePanel(size)}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className={`text-base font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {size} px
                        </h3>
                        {isExpanded ? (
                          <ChevronUp className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                        ) : (
                          <ChevronDown className={`h-5 w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                        )}
                      </div>
                    </div>

                    {/* Banners Grid in Panel */}
                    {isExpanded && (
                      <div className="p-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {sizeBanners.map((banner, index) => (
                            <div
                              key={banner.id}
                              className={`${isDarkMode ? 'bg-black border-gray-800' : 'bg-white border-gray-200'} rounded-lg border shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden`}
                            >
                              {/* Banner Preview */}
                              <div className={`relative ${isDarkMode ? 'bg-black' : 'bg-gray-50'} p-4 flex items-center justify-center min-h-[180px]`}>
                                <div 
                                  className="relative flex items-center justify-center overflow-hidden bg-white rounded-lg shadow-inner"
                                  style={{
                                    width: `${Math.min(banner.width / 2.5, 250)}px`,
                                    height: `${Math.min(banner.height / 2.5, 250)}px`,
                                    maxWidth: '100%',
                                    maxHeight: '250px'
                                  }}
                                >
                                  <img
                                    src={banner.imageUrl}
                                    alt={banner.name}
                                    className="w-full h-full object-contain"
                                    style={{ imageRendering: 'high-quality' }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.parentElement.innerHTML = `<div class="text-gray-400 text-xs text-center p-4">Banner Preview</div>`;
                                    }}
                                  />
                                </div>
                              </div>

                              {/* HTML Code Textbox */}
                              <div className="p-3">
                                <label className={`block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  HTML Code:
                                </label>
                                <textarea
                                  readOnly
                                  value={getBannerHtmlCode(banner)}
                                  className={`w-full px-3 py-2 rounded-lg text-xs font-mono border resize-none ${
                                    isDarkMode 
                                      ? 'bg-black border-gray-700 text-gray-300' 
                                      : 'bg-gray-50 border-gray-300 text-gray-700'
                                  }`}
                                  rows="2"
                                  onClick={(e) => e.target.select()}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubAffiliateBanners;
